import React, { useContext } from 'react';
import { TabsProps } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import DevicesContainer, {
  containerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import AuditLogContainer from '@totem/components/services/remoteAccess/auditLog/AuditLogContainer';
import AuditLogLayout, {
  AuditLogExtra,
} from '@totem/components/services/remoteAccess/auditLog/AuditLogLayout';
import OverlayNetworksContainer from '@totem/components/services/remoteAccess/overlayNetworks/OverlayNetworksContainer';
import GatewayDeviceTable from '@totem/components/services/remoteAccess/roleUsers/rootRole/GatewayDeviceTable';
import CalendarTimeline from '@totem/components/services/remoteAccess/sessions/CalendarTimelineChart';
import RemoteAccessSessionsContainer from '@totem/components/services/remoteAccess/sessions/RemoteAccessSessionsContainer';
import SessionEventsTable from '@totem/components/services/remoteAccess/sessions/SessionActivityTable';
import SessionComboChart from '@totem/components/services/remoteAccess/sessions/SessionComboChart';
import RemoteAccessAuditUsers from '@totem/components/services/remoteAccess/v2/audit/RemoteAccessAuditUsers';
import RemoteAccessAuditUsersContainer from '@totem/components/services/remoteAccess/v2/audit/RemoteAccessAuditUsersContainer';
import NoSiteAccessUsers from '@totem/components/services/remoteAccess/v2/noSiteAccess/NoSiteAccessUsers';
import NoSiteAccessUsersContainer from '@totem/components/services/remoteAccess/v2/noSiteAccess/NoSiteAccessUsersContainer';
import RemoteAccessSummaryV2 from '@totem/components/services/remoteAccess/v2/summary/RemoteAccessSummaryV2';
import RemoteAccessTotalsV2 from '@totem/components/services/remoteAccess/v2/totals/RemoteAccessTotalsV2';
import RemoteAccessTotalsV2Container from '@totem/components/services/remoteAccess/v2/totals/RemoteAccessTotalsV2Container';
import RemoteAccessUsersV2 from '@totem/components/services/remoteAccess/v2/users/RemoteAccessUsersV2';
import RemoteAccessUsersV2Container from '@totem/components/services/remoteAccess/v2/users/RemoteAccessUsersV2Container';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { GATEWAY_PAGINATION_ENDPOINT } from '@totem/utilities/endpoints';

const RemoteAccessDashboardTabs = () => {
  const { userProfile } = useContext(UserProfileContext);

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'summary',
        label: 'Summary',
        children: <RemoteAccessSummaryV2 />,
      },
      {
        key: 'users',
        label: 'Users',
        children: (
          <>
            <RemoteAccessTotalsV2Container>
              <RemoteAccessTotalsV2 />
            </RemoteAccessTotalsV2Container>
            <br />
            <RemoteAccessUsersV2Container>
              <RemoteAccessUsersV2 />
            </RemoteAccessUsersV2Container>
          </>
        ),
      },
      {
        key: 'sessions',
        label: 'Sessions',
        children: (
          <>
            <SessionComboChart />
            <br />
            <CalendarTimeline />
          </>
        ),
      },
      {
        key: 'activity',
        label: 'Activity',
        children: <SessionEventsTable />,
      },
    ];

    if (permissions.remoteAccessAdmin.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'no-site-access',
        label: 'No-Site Access',
        children: (
          <NoSiteAccessUsersContainer>
            <NoSiteAccessUsers />
          </NoSiteAccessUsersContainer>
        ),
      });

      tabItems.push({
        key: 'user-audit',
        label: 'User Audit',
        children: (
          <RemoteAccessAuditUsersContainer>
            <RemoteAccessAuditUsers />
          </RemoteAccessAuditUsersContainer>
        ),
      });
    }

    if (
      permissions.remoteAccessOverlayNetworkAdmin.hasPermissions(
        userProfile,
        securityGroupCheckConstraintAny,
      )
    ) {
      tabItems.push({
        key: 'overlayNetworks',
        label: (
          <IBTooltip
            content={permissions.remoteAccessOverlayNetworkAdmin.tooltip}
          >
            Overlay Networks
          </IBTooltip>
        ),
        children: <OverlayNetworksContainer />,
      });
    }

    if (
      permissions.remoteAccessAdmin.hasPermissions(
        userProfile,
        securityGroupCheckConstraintAny,
      ) &&
      permissions.internal.hasPermissions(userProfile)
    ) {
      tabItems.push({
        key: 'auditLog',
        label: 'Audit Log',
        children: (
          <AuditLogContainer>
            <AuditLogExtra />
            <br />
            <AuditLogLayout />
          </AuditLogContainer>
        ),
      });
    }

    if (
      permissions.internalOrganizationalAdministrator.hasPermissions(
        userProfile,
      )
    ) {
      tabItems.push({
        key: 'gateways',
        label: (
          <IBTooltip
            content={permissions.internalOrganizationalAdministrator.tooltip}
          >
            Gateways
          </IBTooltip>
        ),
        children: <GatewayDeviceTable />,
      });
    }

    return tabItems;
  };

  return (
    <InputContainer
      filterConfig={containerConfiguration}
      defaultSort={{
        field: 'device.displayName',
        direction: 'ascend',
      }}
      staticFilters={[{ field: 'type', values: ['IB Gateway', 'Airwall'] }]}
      componentKey={'dashboard.remoteAccess'}
    >
      <DevicesContainer deviceEndPoint={GATEWAY_PAGINATION_ENDPOINT}>
        <RemoteAccessSessionsContainer
          refresh={false}
          onDataRefreshRequested={() => {}}
        >
          <IBTabs defaultActiveKey="1" items={getTabItems()} />
        </RemoteAccessSessionsContainer>
      </DevicesContainer>
    </InputContainer>
  );
};

export default RemoteAccessDashboardTabs;
